import { useEffect, useState, useCallback } from "react";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import {
  adminListSuggestions, adminApproveSuggestion, adminRejectSuggestion,
} from "../../lib/services";
import { formatApiError } from "../../lib/api";
import useStore from "../../store/useStore";

const STATUSES = ["pending", "approved", "rejected", "all"];

const STATUS_COLOR = {
  approved: "text-acid",
  rejected: "text-white/40",
  pending: "text-white",
};

export default function AdminCredits() {
  const refreshStoreShows = useStore((s) => s.loadServerState);
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const refresh = useCallback(async () => {
    setLoaded(false);
    try {
      const r = await adminListSuggestions(status);
      setItems(r.items || []);
    } finally {
      setLoaded(true);
    }
  }, [status]);

  useEffect(() => { refresh(); }, [refresh]);

  const onApprove = async (id) => {
    try {
      await adminApproveSuggestion(id);
      toast("approved");
      await Promise.all([refresh(), refreshStoreShows()]);
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  const onReject = async (id) => {
    try {
      await adminRejectSuggestion(id, rejectReason);
      toast("rejected");
      setRejectingId(null);
      setRejectReason("");
      await refresh();
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  return (
    <section data-testid="admin-credits" className="px-5 py-5">
      <div className="flex gap-1 mb-4 overflow-x-auto no-scrollbar">
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`admin-credits-tab-${s}`}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider border-2 transition-colors ${
              status === s
                ? "bg-acid text-black border-acid"
                : "border-white/30 text-white/70 hover:border-white"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {!loaded ? (
        <p className="font-mono text-xs text-white/40">loading...</p>
      ) : items.length === 0 ? (
        <p className="font-mono text-xs text-white/40">none.</p>
      ) : (
        <ul className="space-y-4">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`admin-credit-${it.id}`}
              className="border-2 border-white/15 p-3 space-y-2"
            >
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div className="font-display text-base">
                  {(it.show?.title || it.showId).toLowerCase()}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                  by @{it.user?.username || "user"} ·{" "}
                  <span className={STATUS_COLOR[it.status] || "text-white"}>
                    {it.status}
                  </span>
                </div>
              </div>

              <ul className="font-mono text-[11px] text-white/80 space-y-1">
                {Object.entries(it.proposed || {}).map(([k, v]) => (
                  <li key={k} className="flex gap-2">
                    <span className="text-white/40 w-28 shrink-0">{k}</span>
                    <span className="break-words flex-1">
                      {Array.isArray(v)
                        ? v.map((x) => typeof x === "object" ? `${x.role}: ${x.name}` : x).join(", ")
                        : String(v)}
                    </span>
                  </li>
                ))}
              </ul>

              {it.status === "rejected" && it.rejectionReason && (
                <p className="font-mono text-[10px] text-white/40 italic">
                  rejected: {it.rejectionReason}
                </p>
              )}

              {it.status === "pending" && (
                <div className="flex gap-2 flex-wrap">
                  <button
                    data-testid={`admin-credit-approve-${it.id}`}
                    onClick={() => onApprove(it.id)}
                    className="font-mono text-[11px] lowercase border-2 border-acid text-acid bg-acid/10 px-3 py-1.5 hover:bg-acid hover:text-black transition-colors flex items-center gap-1.5"
                  >
                    <Check className="w-3.5 h-3.5" />
                    approve
                  </button>
                  <button
                    data-testid={`admin-credit-reject-${it.id}`}
                    onClick={() => setRejectingId(it.id)}
                    className="font-mono text-[11px] lowercase border-2 border-white/30 text-white/70 px-3 py-1.5 hover:border-white hover:text-white transition-colors flex items-center gap-1.5"
                  >
                    <X className="w-3.5 h-3.5" />
                    reject
                  </button>
                </div>
              )}

              {rejectingId === it.id && (
                <div className="border-t border-white/15 pt-3 mt-2 space-y-2">
                  <textarea
                    data-testid={`admin-credit-reject-reason-${it.id}`}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={2}
                    placeholder="reason (optional)"
                    className="w-full bg-transparent border border-white/30 px-2 py-1.5 font-mono text-xs focus:border-acid outline-none resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      data-testid={`admin-credit-confirm-reject-${it.id}`}
                      onClick={() => onReject(it.id)}
                      className="font-mono text-[10px] uppercase tracking-wider bg-white text-black px-3 py-1.5"
                    >
                      confirm reject
                    </button>
                    <button
                      onClick={() => { setRejectingId(null); setRejectReason(""); }}
                      className="font-mono text-[10px] uppercase tracking-wider text-white/60 px-2 py-1.5"
                    >
                      cancel
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
